import React, { useState } from 'react';
import { FileUpIcon, CheckCircleIcon, BrainIcon, XIcon } from 'lucide-react';
interface Step1BrochureProps {
  formData: any;
  updateFormData: (data: any) => void;
}
const Step1Brochure: React.FC<Step1BrochureProps> = ({
  formData,
  updateFormData
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiExtracted, setAiExtracted] = useState(false);
  // Mock AI extracted data
  const mockExtractedData = {
    programName: 'Master of Business Administration',
    programType: "Master's",
    description: 'Our MBA program is designed to develop the skills required for careers in business and management. The program integrates theoretical and practical knowledge of business with an international perspective.',
    learningOutcomes: ['Demonstrate advanced knowledge of business concepts and theories', 'Apply strategic thinking to complex business problems', 'Develop leadership skills for diverse business environments', 'Analyze financial data to make informed business decisions'],
    targetStudents: ['Professionals', 'Business Graduates', 'Career Changers'],
    deliveryMode: 'Hybrid',
    duration: '2 Years',
    intakeDates: ['September 2023', 'January 2024'],
    tuition: '120,000 AED',
    qualifications: ["Bachelor's degree", '2+ years work experience', 'English proficiency'],
    accreditation: 'AACSB Accredited',
    campusLocation: 'Dubai Business District'
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
      }
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };
  const handleRemoveFile = () => {
    setFile(null);
    setAiExtracted(false);
  };
  const handleAnalyzeWithAI = () => {
    setIsAnalyzing(true);
    // Simulate AI analysis with a timeout
    setTimeout(() => {
      setIsAnalyzing(false);
      setAiExtracted(true);
      // Update form data with extracted information
      updateFormData({
        brochureFile: file,
        aiExtracted: true,
        // Add extracted data to form
        programName: mockExtractedData.programName,
        programType: mockExtractedData.programType,
        description: mockExtractedData.description,
        learningOutcomes: mockExtractedData.learningOutcomes,
        targetStudents: mockExtractedData.targetStudents,
        deliveryMode: mockExtractedData.deliveryMode,
        duration: mockExtractedData.duration,
        intakeDates: mockExtractedData.intakeDates,
        tuition: mockExtractedData.tuition,
        qualifications: mockExtractedData.qualifications,
        accreditation: mockExtractedData.accreditation,
        campusLocation: mockExtractedData.campusLocation
      });
    }, 2000);
  };
  return <div data-id="element-323">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-324">
        Upload Program Brochure
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-325">
        Upload a PDF brochure to automatically extract program details using AI,
        or skip this step to enter information manually.
      </p>
      {!file ? <div className={`border-2 border-dashed rounded-lg p-12 text-center ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-326">
          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-327">
            <FileUpIcon className="w-8 h-8 text-gray-400" data-id="element-328" />
          </div>
          <p className="text-gray-600 mb-2" data-id="element-329">
            Drag and drop your PDF brochure here
          </p>
          <p className="text-gray-500 text-sm mb-4" data-id="element-330">or</p>
          <label className="bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 cursor-pointer" data-id="element-331">
            Browse Files
            <input type="file" accept=".pdf" className="hidden" onChange={handleFileChange} data-id="element-332" />
          </label>
          <p className="text-gray-400 text-xs mt-4" data-id="element-333">
            Supported format: PDF (Max 10MB)
          </p>
        </div> : <div data-id="element-334">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 mb-6" data-id="element-335">
            <div className="flex items-center" data-id="element-336">
              <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center mr-3" data-id="element-337">
                <FileUpIcon className="w-5 h-5 text-primary-500" data-id="element-338" />
              </div>
              <div data-id="element-339">
                <p className="text-sm font-medium text-gray-800" data-id="element-340">{file.name}</p>
                <p className="text-xs text-gray-500" data-id="element-341">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button type="button" onClick={handleRemoveFile} className="p-1 rounded-full hover:bg-gray-200" data-id="element-342">
              <XIcon className="w-5 h-5 text-gray-500" data-id="element-343" />
            </button>
          </div>
          {!aiExtracted ? <div className="text-center" data-id="element-344">
              <button type="button" onClick={handleAnalyzeWithAI} disabled={isAnalyzing} className={`bg-primary-500 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-primary-600 flex items-center justify-center mx-auto ${isAnalyzing ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-345">
                {isAnalyzing ? <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" data-id="element-346"></div>
                    Analyzing Document...
                  </> : <>
                    <BrainIcon className="w-5 h-5 mr-2" data-id="element-347" />
                    Analyze with AI
                  </>}
              </button>
              <p className="text-gray-500 text-sm mt-4" data-id="element-348">
                Our AI will extract program details to save you time
              </p>
            </div> : <div className="bg-green-50 border border-green-100 rounded-lg p-4" data-id="element-349">
              <div className="flex items-start" data-id="element-350">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-351" />
                <div data-id="element-352">
                  <h3 className="font-medium text-green-800 mb-1" data-id="element-353">
                    AI Analysis Complete
                  </h3>
                  <p className="text-sm text-green-700 mb-3" data-id="element-354">
                    We've extracted program details from your brochure. You can
                    review and edit this information in the next steps.
                  </p>
                  <div className="bg-white p-4 rounded border border-green-100 mb-3" data-id="element-355">
                    <h4 className="font-medium text-gray-800 mb-2" data-id="element-356">
                      Extracted Information Preview:
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-357">
                      <div data-id="element-358">
                        <p className="text-gray-500" data-id="element-359">Program Name:</p>
                        <p className="font-medium" data-id="element-360">
                          {mockExtractedData.programName}
                        </p>
                      </div>
                      <div data-id="element-361">
                        <p className="text-gray-500" data-id="element-362">Program Type:</p>
                        <p className="font-medium" data-id="element-363">
                          {mockExtractedData.programType}
                        </p>
                      </div>
                      <div data-id="element-364">
                        <p className="text-gray-500" data-id="element-365">Duration:</p>
                        <p className="font-medium" data-id="element-366">
                          {mockExtractedData.duration}
                        </p>
                      </div>
                      <div data-id="element-367">
                        <p className="text-gray-500" data-id="element-368">Delivery Mode:</p>
                        <p className="font-medium" data-id="element-369">
                          {mockExtractedData.deliveryMode}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-370">
                    <div className="flex items-center mr-4" data-id="element-371">
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-1" data-id="element-372"></div>
                      <span className="text-xs text-gray-600" data-id="element-373">
                        High confidence (80%+)
                      </span>
                    </div>
                    <div className="flex items-center mr-4" data-id="element-374">
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1" data-id="element-375"></div>
                      <span className="text-xs text-gray-600" data-id="element-376">
                        Medium confidence (50-80%)
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-377">
                      <div className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-378"></div>
                      <span className="text-xs text-gray-600" data-id="element-379">
                        Low confidence (&lt;50%)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>}
      <div className="mt-8 border-t border-gray-200 pt-6" data-id="element-380">
        <p className="text-gray-500 text-sm" data-id="element-381">
          <span className="font-medium" data-id="element-382">Note:</span> This step is optional. You
          can skip to the next step if you prefer to enter program details
          manually.
        </p>
      </div>
    </div>;
};
export default Step1Brochure;